import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";

export default function ThankYouPage() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const transactionId = params.get("transactionId");
    const { t } = useTranslation();
    const [status, setStatus] = useState("checking");

    useEffect(() => {
        let stopped = false;
        let attempts = 0;
        let interval = null;

        const maxAttempts = 20;

        const stopWithStatus = (nextStatus) => {
            stopped = true;
            setStatus(nextStatus);
            if (interval) clearInterval(interval);
        };

        const incrementAttemptsOrTimeout = () => {
            attempts += 1;

            if (attempts >= maxAttempts) {
                stopWithStatus("timeout");
            }
        };

        const verifyMonCash = async () => {
            if (stopped) return;

            try {
                const { data } = await api.get("/api/payments/moncash/verify-payment", {
                    params: { transactionId },
                });

                if (stopped) return;

                if (["processed", "completed", "success"].includes(data.status)) {
                    stopWithStatus("success");
                } else if (["failed", "error", "ignored"].includes(data.status)) {
                    stopWithStatus("failed");
                } else {
                    incrementAttemptsOrTimeout();
                }
            } catch (e) {
                if (!stopped) incrementAttemptsOrTimeout();
            }
        };

        const checkStripe = async () => {
            if (stopped) return;

            try {
                const { data } = await api.get(`/api/payments/checkout/status/${sessionId}`);

                if (stopped) return;

                if (data.payment_status === "paid") {
                    stopWithStatus("success");
                } else if (data.status === "expired") {
                    stopWithStatus("failed");
                } else {
                    incrementAttemptsOrTimeout();
                }
            } catch (e) {
                if (!stopped) incrementAttemptsOrTimeout();
            }
        };

        if (transactionId) {
            verifyMonCash();
            interval = setInterval(verifyMonCash, 2000);
        } else if (sessionId) {
            checkStripe();
            interval = setInterval(checkStripe, 2000);
        } else {
            setStatus("failed");
        }

        return () => {
            stopped = true;
            if (interval) clearInterval(interval);
        };
    }, [sessionId, transactionId]);

    return (
        <div data-testid="thank-you-page" className="min-h-[60vh] flex items-center justify-center px-4 py-16">
            <div className="bento-card p-8 md:p-12 max-w-lg w-full text-center space-y-6">
                {status === "checking" && (
                    <>
                        <Loader2 className="h-16 w-16 mx-auto animate-spin text-muted-foreground" />
                        <h1 className="font-heading font-black text-2xl">{t("thank_you.processing")}</h1>
                    </>
                )}
                {status === "success" && (
                    <>
                        <div className="w-20 h-20 mx-auto rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
                            <CheckCircle className="h-12 w-12 text-[hsl(var(--success))]" />
                        </div>
                        <h1 data-testid="thankyou-success-title" className="font-heading font-black text-3xl md:text-4xl">
                            {t("thank_you.title")}
                        </h1>
                        <p className="text-muted-foreground">{t("thank_you.subtitle")}</p>
                        <Link to="/bookings" data-testid="thankyou-view-bookings" className="btn-primary inline-block">
                            {t("thank_you.cta")}
                        </Link>
                    </>
                )}
                {(status === "failed" || status === "timeout") && (
                    <>
                        <div className="w-20 h-20 mx-auto rounded-full bg-[hsl(var(--destructive))]/10 flex items-center justify-center">
                            <XCircle className="h-12 w-12 text-[hsl(var(--destructive))]" />
                        </div>
                        <h1 className="font-heading font-black text-3xl">{t("thank_you.failed")}</h1>
                        <Link to="/" className="btn-outline inline-block">
                            {t("nav.home")}
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
